/* eslint-disable stylistic/comma-dangle */

/**
 * Header Theme Styles
 *
 * Uses variant system for consistent styling across header participants:
 * - variant="light": Black icons/text (for transparent, solid states)
 * - variant="dark": White icons/text (for gradient state)
 *
 * headerState determines background:
 * - transparent: No background
 * - gradient: Dark gradient background with box-shadow fade
 * - solid: Solid white background
 *
 * @see task-sticky-header.md for requirements
 */

import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';
import {A11Y_ICON_GLYPH_SIZE, A11Y_ICON_TARGET_MIN, HEADER_ICON_HIT} from '@amblique/king-living/app/theme/foundations/a11y-targets';

/**
 * Color tokens for light variant (transparent, solid states)
 */
const lightColors = {
  text: 'greyscale.90',
  icon: 'greyscale.90',
  border: 'brandColors.greyscale10',
  locationText: 'greyscale.90',
};

/**
 * Color tokens for dark variant (gradient / overlay-on-hero state)
 */
const darkColors = {
  text: 'text.white',
  icon: 'text.white',
  border: 'white',
  locationText: 'text.white',
};

/**
 * Get variant-specific color styles for all header parts
 * @param {Object} colors - Color tokens object
 * @returns {Object} Variant styles for header parts
 */
const getVariantStyles = (colors) => ({
  icons: {
    color: colors.icon,
  },
  searchIcon: {
    color: colors.icon,
  },
  nav: {
    color: colors.text,
  },
  border: {
    borderColor: colors.border,
  },
  buttonLocationText: {
    color: colors.locationText,
  },
});

export default {
  baseStyle: ({headerState = 'solid', originalState, isSticky = false, isHeaderHovered = false}) => {
    const isTransparent = headerState === 'transparent';
    const isGradient = headerState === 'gradient';
    const isGradientHover = isGradient && isHeaderHovered && !isSticky;
    // Hairline only when the page is not configured as gradient (sticky / menu-open
    // can flip headerState to solid — that must not add a border on overlay pages).
    const isConfiguredGradient = (originalState || headerState) === 'gradient';
    const showBottomBorder = !isConfiguredGradient;

    // Dynamic background based on header state
    const getBackground = () => {
      if (isGradientHover) {
        return 'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.1) 100%), linear-gradient(90deg, rgba(27, 26, 24, 0.5) 0%, rgba(27, 26, 24, 0.5) 100%)';
      }

      if (isSticky && !isGradient) {
        return 'rgba(247, 245, 240, 0.66)';
      }

      if (isTransparent) return 'transparent';
      // Gradient state: transparent background, gradient is handled by ::after pseudo-element
      if (isGradient) return 'transparent';

      return 'background.primary'; // solid
    };

    // Sticky / solid have a keyline; overlay-on-hero does not.
    const getBorderColor = () => {
      if (!showBottomBorder) return 'transparent';

      return 'brandColors.greyscale10';
    };

    return {
      container: {
        minWidth: 'xs',
        width: 'full',
        background: getBackground(),
        backdropFilter: isGradientHover || (isSticky && !isGradient) ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isGradientHover || (isSticky && !isGradient) ? 'blur(20px)' : undefined,
        transition: 'background 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease',
        position: 'relative',
        borderBottom: showBottomBorder ? '1px solid' : 'none',
        borderColor: getBorderColor(),
        ...(isGradient && {
          zIndex: 10,
          // Keep visible so the account popover can sit below the bar. Hover glass
          // is clipped by the header box itself (backdrop-filter), not overflow.
          overflow: 'visible',
        }),
        // Gradient overlay extending below header using ::after pseudo-element
        ...(isGradient && !isGradientHover && {
          _after: {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '120px',
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.46) 34%, rgba(0, 0, 0, 0) 100%)',
            pointerEvents: 'none',
            zIndex: -1,
          },
        }),
      },
      content: {
        maxWidth: '1440px',
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingX: {base: '16px', lg: '80px'},
        paddingTop: 0,
        paddingBottom: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: {base: '56px', lg: '68px'},
        position: 'relative',
        zIndex: 1,
      },
      headerBar: {
        alignItems: 'center',
        height: {base: '56px', lg: '68px'},
        minHeight: {base: '56px', lg: '68px'},
        width: '100%',
      },
      headerLeft: {
        columnGap: {base: '8px', lg: '24px'},
        zIndex: 1,
      },
      headerPrimaryCluster: {
        alignItems: 'center',
        display: 'flex',
        sx: {
          columnGap: '8px',
          gap: '8px',
          '@media screen and (min-width: 992px)': {
            columnGap: '24px',
            gap: '24px',
            '&:hover .header-primary-nav__item:not(:hover)': {
              opacity: 0.4,
            },
            '&:hover [data-header-search-trigger]:not(:hover)': {
              opacity: 0.4,
            },
          },
        },
      },
      headerRight: {
        justifyContent: 'flex-end',
        marginLeft: 'auto',
        zIndex: 1,
      },
      searchContainer: {
        order: [2, 2, 2, 'inherit'],
        width: ['full', 'full', 'full', 60],
        marginRight: [0, 0, 0, 4],
        marginBottom: [1, 1, 2, 0],
      },
      bodyContainer: {
        flex: '1',
      },
      logo: {
        width: '100%',
        height: 'auto',
        ...(isGradient && {color: 'text.white'}),
      },
      iconLogo: {
        display: 'flex',
        left: '50%',
        marginLeft: 0,
        maxWidth: {base: '75px', lg: '93px'},
        maxHeight: {base: '16px', lg: '20px'},
        width: {base: '75px', lg: '93px'},
        height: {base: '16px', lg: '20px'},
        minWidth: 'auto',
        padding: 0,
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 2,
        ...A11Y_FOCUS_VISIBLE,
      },
      icons: {
        alignItems: 'center',
        display: 'flex',
        flexShrink: 0,
        height: {base: HEADER_ICON_HIT, lg: '32px'},
        justifyContent: 'center',
        minHeight: {base: HEADER_ICON_HIT, lg: '32px'},
        minWidth: {base: HEADER_ICON_HIT, lg: '32px'},
        p: '8px',
        width: {base: HEADER_ICON_HIT, lg: '32px'},
        transition: 'color 0.3s ease',
        ...A11Y_FOCUS_VISIBLE,
        ...(isGradient && {color: 'text.white'}),
        sx: {
          svg: {
            ...(isGradient && {color: 'text.white'}),
          }
        }
      },
      cartIcon: {
        overflow: 'visible',
        position: 'relative',
        height: {base: HEADER_ICON_HIT, lg: '32px'},
        minHeight: {base: HEADER_ICON_HIT, lg: '32px'},
        minWidth: {base: HEADER_ICON_HIT, lg: '32px'},
        width: {base: HEADER_ICON_HIT, lg: '32px'},
        sx: {
          '.chakra-button__icon': {
            overflow: 'visible',
          },
          svg: {
            height: '22px',
            width: '22px',
          },
        },
      },
      searchIcon: {
        boxSize: A11Y_ICON_GLYPH_SIZE,
        flexShrink: 0,
        display: 'block',
        height: A11Y_ICON_GLYPH_SIZE,
        width: A11Y_ICON_GLYPH_SIZE,
        ...(isGradient && {color: 'text.white'}),
      },
      searchIconButton: {
        alignItems: 'center',
        display: 'flex',
        fontSize: A11Y_ICON_GLYPH_SIZE,
        height: {base: HEADER_ICON_HIT, lg: '32px'},
        justifyContent: 'center',
        minHeight: {base: HEADER_ICON_HIT, lg: '32px'},
        minWidth: {base: HEADER_ICON_HIT, lg: '32px'},
        p: '8px',
        position: 'relative',
        transition: 'opacity 0.2s ease',
        width: {base: HEADER_ICON_HIT, lg: '32px'},
        ...A11Y_FOCUS_VISIBLE,
        ...(isGradient && {color: 'text.white'}),
        _hover: {
          bg: 'transparent',
        },
        _active: {
          bg: 'transparent',
        },
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
        },
        sx: {
          '.chakra-icon': {
            height: A11Y_ICON_GLYPH_SIZE,
            width: A11Y_ICON_GLYPH_SIZE,
            ...(isGradient && {color: 'text.white'}),
          },
        },
      },
      cartBadge: {
        alignItems: 'center',
        bg: isGradient ? 'white' : 'brandColors.primaryText',
        border: 'none',
        borderRadius: 'full',
        boxSizing: 'border-box',
        color: isGradient ? 'brandColors.primaryText90' : 'white',
        display: 'inline-flex',
        flexShrink: 0,
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '11px',
        fontWeight: 500,
        h: '16px',
        justifyContent: 'center',
        left: 'calc(50% + 8px)',
        lineHeight: 1.2,
        minW: '16px',
        overflow: 'hidden',
        paddingX: '0',
        paddingY: '3px',
        position: 'absolute',
        textTransform: 'none',
        top: 'calc(50% + 8px)',
        transform: 'translate(-50%, -50%)',
        w: 'auto',
        zIndex: 1,
      },
      // Gradient overlay (only visible in gradient state)
      gradientOverlay: {
        display: isGradient && !isGradientHover ? 'block' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '120px',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.46) 0%, transparent 100%)',
        pointerEvents: 'none',
        zIndex: -1,
      },
      // Navigation text styles
      nav: {
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: 1.4,
        transition: 'color 0.3s ease',
        ...(isGradient && {color: 'text.white'}),
      },
      // Border styles (for dividers)
      border: {
        transition: 'border-color 0.3s ease',
        ...(isGradient && {borderColor: 'white'}),
      },
      buttonLocationIcon: {
        alignItems: 'center',
        columnGap: '8px',
        display: 'flex',
        flexShrink: 0,
        height: {base: HEADER_ICON_HIT, lg: 'auto'},
        justifyContent: 'center',
        minHeight: {base: HEADER_ICON_HIT, lg: 'auto'},
        minWidth: {base: HEADER_ICON_HIT, lg: 'auto'},
        p: {base: '5px', lg: 0},
        width: {base: HEADER_ICON_HIT, lg: 'auto'},
        ...A11Y_FOCUS_VISIBLE,
        ...(isGradient && {color: 'text.white'}),
        sx: {
          svg: {
            height: {'base': '22px', 'lg': '16px'},
            width: {'base': '22px', 'lg': '16px'},
          },
        },
      },
      locationPinIcon: {
        boxSize: 'unset',
        flexShrink: 0,
        height: '16px',
        width: '16px',
        ...(isGradient && {color: 'text.white'}),
      },
      buttonLocationText: {
        color: isGradient ? 'text.white' : 'greyscale.90',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '1.4',
        textDecoration: 'underline',
        textDecorationColor: 'currentColor',
        textDecorationThickness: '1px',
        textUnderlineOffset: '3px',
        whiteSpace: 'nowrap',
        transition: 'color 0.3s ease',
      },
      iconHamburger: {
        display: {base: 'flex', lg: 'none'},
        sx: {
          svg: {
            height: '24px',
            width: '24px',
          },
        },
      },
      actionsContainer: {
        display: 'flex',
        alignItems: 'center',
        columnGap: {base: '8px', lg: '32px'},
        sx: {
          '> *:empty': {
            display: 'none',
          },
        }
      },
      headerUtilityItems: {
        alignItems: 'center',
        columnGap: {base: '8px', lg: '16px'},
        display: 'flex',
      },
      accountIcon: {
        display: {base: 'none', lg: 'inline-flex'},
        height: '32px',
        minHeight: '32px',
        minWidth: '32px',
        width: '32px',
        cursor: 'pointer',
        p: '8px',
        alignSelf: 'auto',
        ...A11Y_FOCUS_VISIBLE,
        sx: {
          svg: {
            height: '20px',
            width: '20px',
          },
        },
      },
      accountIconDesktop: {
        display: {base: 'none', lg: 'inline'},
        height: '32px',
        minHeight: '32px',
        minWidth: '32px',
        width: '32px',
        cursor: 'pointer',
        alignSelf: 'auto',
        ...A11Y_FOCUS_VISIBLE,
      },
      localeSelectorContainer: {
        display: {base: 'none', lg: 'inline-flex'},
        sx: {
          '&:empty': {
            display: 'none',
          },
        },
      },
      accountMenuPopover: {
        boxShadow: 'none',
      },
      popoverContent: {
        bg: 'white',
        border: '1px solid',
        borderColor: 'brandColors.greyscale20',
        borderRadius: '4px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
        zIndex: 1500,
        p: 0,
        overflow: 'hidden',
      },
      accountNameBadge: {
        alignItems: 'center',
        bg: 'transparent',
        color: isGradient ? 'text.white' : 'brandColors.itemMenu',
        cursor: 'pointer',
        display: 'inline-flex',
        height: '32px',
        justifyContent: 'center',
        minHeight: '32px',
        minWidth: '32px',
        p: '8px',
        transition: 'color 0.3s ease',
        width: '32px',
        ...A11Y_FOCUS_VISIBLE,
      },
      accountNameBadgeInner: {
        alignItems: 'center',
        bg: 'transparent',
        border: '1.5px solid',
        borderColor: 'currentColor',
        borderRadius: 'full',
        color: 'inherit',
        display: 'inline-flex',
        flexShrink: 0,
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '10px',
        fontWeight: 500,
        height: '20px',
        justifyContent: 'center',
        letterSpacing: 0,
        lineHeight: 1.4,
        minHeight: '20px',
        minWidth: '20px',
        width: '20px',
      },
      accountPopoverUserBlock: {
        px: 4,
        pt: 4,
        pb: 3,
      },
      accountPopoverAccountLink: {
        alignItems: 'center',
        bg: 'transparent',
        border: 0,
        borderRadius: 0,
        color: 'brandColors.dark',
        display: 'flex',
        justifyContent: 'flex-start',
        fontSize: '16px',
        fontWeight: '600',
        gap: 4,
        minHeight: '66px',
        textAlign: 'left',
        textDecoration: 'none',
        width: '100%',
        ...A11Y_FOCUS_VISIBLE,
        sx: {
          '&': {
            padding: '16px',
            textDecoration: 'none',
          },
          '&:hover, &:focus-visible': {
            textDecoration: 'none',
          }
        },
      },
      accountPopoverContentBody: {
        boxShadow: 'none',
        padding: '8px 0',
      },
      accountPopoverText: {
        color: 'brandColors.dark',
        fontSize: '16px',
        fontWeight: '600',
      },
      accountPopoverDivider: {
        borderColor: '#E8E8E8',
      },
      accountPopoverEmailText: {
        color: 'greyscale.70',
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '120%',
      },
      arrowDown: {
        height: A11Y_ICON_TARGET_MIN,
        minHeight: A11Y_ICON_TARGET_MIN,
        minWidth: A11Y_ICON_TARGET_MIN,
        width: A11Y_ICON_TARGET_MIN,
        marginRight: 0,
        alignSelf: ['self-start', 'self-start', 'self-start', 'auto'],
        cursor: 'pointer',
        _focus: {
          boxShadow: 'outline',
        },
        _focusVisible: {
          outline: 0,
        },
        display: ['none', 'none', 'none', 'block'],
      },
      wishlistIconDesktop: {
        display: {base: 'none', lg: 'flex'},
      },
      signout: {
        width: '100%',
        borderRadius: '4px',
        _hover: {
          background: 'gray.50',
        },
      },
      accountPopoverAccountIcon: {
        color: 'brandColors.primaryDarkLinen',
      },
      languageIconDesktop: {
        display: {base: 'none', lg: 'inline-flex'},
        sx: {
          '.chakra-icon': {
            width: '24px',
            height: '24px',
          }
        }
      },
    };
  },

  /**
   * Variants for header color scheme
   * - light: Black icons/text (for transparent, solid states)
   * - dark: White icons/text (for gradient state)
   */
  variants: {
    light: getVariantStyles(lightColors),
    dark: getVariantStyles(darkColors),
  },

  /**
   * Default variant (light = black text/icons)
   */
  defaultProps: {
    variant: 'light',
  },
  parts: [
    'container',
    'content',
    'headerBar',
    'headerBar',
    'headerLeft',
    'headerPrimaryCluster',
    'headerRight',
    'actionsContainer',
    'headerUtilityItems',
    'searchContainer',
    'bodyContainer',
    'logo',
    'icons',
    'cartIcon',
    'cartBadge',
    'searchIcon',
    'searchIconButton',
    'signout',
    'signoutIcon',
    'signoutText',
    'languageIconDesktop',
    'wishlistIconDesktop',
    'iconHamburger',
    'buttonLocationIcon',
    'buttonLocationText',
    'locationPinIcon',
    'gradientOverlay',
    'nav',
    'border',
    'accountIcon',
    'accountIconDesktop',
    'accountNameBadge',
    'accountNameBadgeInner',
    'iconLogo',
    'popoverContent',
    'accountPopoverUserBlock',
    'accountPopoverAccountLink',
    'accountPopoverDivider',
    'accountPopoverEmailText',
    'arrowDown',
  ],
};
